import React, { useRef, useEffect } from 'react';
import useDidUpdate from '../hooks/useDidUpdate';
import { AnimateHeightProps } from './animateHeight.types';
import { ContentWrapper } from './animateHeight.style';
import Handler from '../../commonTypes/handler.types';
import bindHandlers from '../../utils/handlerHelper';

const TRANSITIONEND: string = 'transitionend';
const TRANSITIONSTART: string = 'transitionstart';

const onTransitionEndCollapse = (e: Event) => {
  const currentTarget = e.currentTarget as HTMLElement;
  currentTarget.removeEventListener(TRANSITIONEND, onTransitionEndCollapse);
  currentTarget.style.visibility = 'hidden';
};

const onTransitionEndExpand = (e: Event) => {
  const currentTarget = e.currentTarget as HTMLElement;
  currentTarget.removeEventListener(TRANSITIONEND, onTransitionEndExpand);
  currentTarget.style.height = '';
};

const collapseContent = (element: HTMLElement) => {
  const contentHeight = element.scrollHeight;
  window.requestAnimationFrame(() => {
    element.style.height = `${contentHeight}px`;
    window.requestAnimationFrame(() => {
      element.style.height = '0px';
    });
  });

  element.removeEventListener(TRANSITIONEND, onTransitionEndExpand);
  element.addEventListener(TRANSITIONEND, onTransitionEndCollapse);
};

const expandContent = (element: HTMLElement) => {
  const contentHeight = element.scrollHeight;
  element.style.visibility = 'visible';
  element.style.height = `${contentHeight}px`;

  element.removeEventListener(TRANSITIONEND, onTransitionEndCollapse);
  element.addEventListener(TRANSITIONEND, onTransitionEndExpand);
};

const AnimateHeight = ({
  expanded = false,
  collapsible,
  transitionDuration = 0.3,
  easing = 'ease-out',
  handlers = {},
  ...otherProps
}: AnimateHeightProps) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const initialStyle = useRef<object>({
    height: expanded ? 'auto' : 0,
    visibility: expanded ? 'visible' : 'hidden',
  });

  useDidUpdate(() => {
    const contentElement = contentRef?.current;
    if (contentElement && typeof window !== 'undefined') {
      // client side only

      if (expanded) {
        expandContent(contentElement);
      } else {
        collapseContent(contentElement);
      }
    }
  }, [expanded, contentRef]);

  useEffect(() => {
    if (!contentRef.current) {
      return;
    }

    const onTransitionStart = (e: Event) => {
      if (handlers.onTransitionStart) {
        handlers.onTransitionStart(e);
      }
    };

    contentRef.current.addEventListener(TRANSITIONSTART, onTransitionStart);

    return () => contentRef.current?.removeEventListener(TRANSITIONSTART, onTransitionStart);
  }, [contentRef.current, handlers.onTransitionStart]);

  const combinedHandlers: Handler = bindHandlers(null, handlers);

  return (
    <ContentWrapper
      ref={contentRef}
      style={initialStyle.current}
      transitionDuration={transitionDuration}
      easing={easing}
      onTransitionEnd={(e: Event) => combinedHandlers.onTransitionEnd && combinedHandlers.onTransitionEnd(e)}
      {...otherProps}
    >
      {collapsible}
    </ContentWrapper>
  );
};

export default AnimateHeight;
